import { ProyectosPlataformaService } from "../../../shared/generated/grpc/plataforma_proyectos_pb";
import { CatalogoItemResponse } from "../../../shared/response/proyectos/CatalogoItemResponse";
import { ItemPropioPayload } from "../../../shared/services/api/payload/proyectos/ItemPropioPayload";
import { ServiceSaveBase } from "../../../shared/services/base/ServiceSaveBase";
import { grpcCall, grpcClient } from "../../../shared/services/grpc/grpcCall";
import { toCatalogoItemResponse } from "./CatalogoService";

/**
 * Ítems propios de un beneficiario: crear uno, reusar uno que ya existe y soltarlo.
 *
 * **Sin `cache`** a propósito. `CatalogoService` implementa `IListCache` sobre `_byCultivoId`, que es la
 * lista que usa la pantalla del catálogo maestro: hacer `upsert` ahí metería el ítem de un beneficiario
 * en el catálogo que ven todos. El wizard mete el ítem devuelto en su propia lista.
 */
export class ItemPropioSaveService extends ServiceSaveBase<CatalogoItemResponse, ItemPropioPayload> {
  /** gRPC-Web (`GuardarItemPropio`). Con `catalogoItemId` reusa el ítem; sin él lo crea. Idempotente. */
  protected async sendSave(p: ItemPropioPayload): Promise<CatalogoItemResponse> {
    const r = await grpcCall(() => grpcClient(ProyectosPlataformaService).guardarItemPropio({
      clienteId: p.clienteId,
      motivo: p.motivo,
      catalogoItemId: p.catalogoItemId ?? undefined,
      cultivoId: p.cultivoId,
      faseId: p.faseId,
      sub: p.sub,
      nombre: p.nombre,
      categoria: p.categoria,
      unidad: p.unidad,
      cantUnidad: p.cantUnidad,
      cantidadPorHa: p.cantidadPorHa,
      tipoP: p.tipoP,
      precioReferencial: p.precioReferencial !== null ? BigInt(Math.round(p.precioReferencial)) : undefined,
      freq: p.freq,
      freqUnidad: p.freqUnidad ?? undefined,
    }));
    return toCatalogoItemResponse(r);
  }

  /**
   * Ítems propios del cultivo con nombre parecido (`BuscarItemsPropiosParecidos`), para ofrecer reusar uno
   * en vez de crear otro igual. Es el paso que evita que esto se vuelve un depósito de nombres repetidos.
   *
   * Sin conexión devuelve lista vacía en vez de fallar: no poder sugerir un reuso no debe impedir crear
   * el ítem.
   */
  async buscarParecidos(cultivoId: string, nombre: string): Promise<CatalogoItemResponse[]> {
    try {
      const r = await grpcCall(() => grpcClient(ProyectosPlataformaService).buscarItemsPropiosParecidos({ cultivoId, nombre }));
      return r.items.map(toCatalogoItemResponse);
    } catch (error) {
      console.warn(`[ItemPropioSaveService.buscarParecidos] no se pudieron buscar ítems parecidos a "${nombre}"; se sigue sin sugerencias`, error);
      return [];
    }
  }

  /** Suelta el ítem de ese beneficiario (`QuitarItemPropio`). El ítem sigue existiendo para sus otros dueños. */
  async quitar(catalogoItemId: string, clienteId: string): Promise<void> {
    await grpcCall(() => grpcClient(ProyectosPlataformaService).quitarItemPropio({ catalogoItemId, clienteId }));
  }
}
